import { ParticlesSettings } from "./ParticlesSettings";
import { Game, rnd } from "../../../../Game";



export class ParticlesEntity {
    static readonly MAX_PARTICLE_CNT = 100;

    private static readonly _MESH_V_SHADERS: string[] = [
        `attribute vec3 aPosition;
        attribute vec2 aUv0;

        uniform mat4 matrix_model;
        uniform mat4 matrix_viewProjection;

        varying vec3 vPositionW;
        varying vec2 vUv0;

        void main(void) {
            vUv0 = aUv0;

            // Vypocitat pozici vrcholu v hernim svete
            vec4 posW = matrix_model * vec4(aPosition, 1.0);
            vPositionW = posW.xyz;

            gl_Position = matrix_viewProjection * posW;
        }`];

    private static readonly _MESH_F_SHADERS: string[] = [
        `varying vec3 vPositionW;
        varying vec2 vUv0;

        uniform vec3 uColor;
        uniform float uMinAlphaZ;
        uniform float uMaxAlphaZ;                

        void main(void) {
            // Vypocitat alfu podle pozice pixelu na ose Z v hernim svete
            float alpha = clamp((vPositionW.z - uMinAlphaZ) / (uMaxAlphaZ - uMinAlphaZ), 0.0, 1.0);

            // Vytvorit ocas castice            
            alpha *= (1.0 - vUv0.y);

            // Plna barva pouze na stredu uv.x
            alpha *= (smoothstep(0.0, 0.5, vUv0.x) - smoothstep(0.5, 1.0, vUv0.x));

            gl_FragColor = vec4(uColor, alpha);
        }`];

    private static _materials: pc.Material[];
    private static _positionBuf: Float32Array;
    private static _indexBuf: Uint32Array;
    private static _uvsBuf: Float32Array;

    private _colorBuf: Float32Array = new Float32Array(3);

    private _mesh: pc.Mesh;
    private _meshSizeZ: number;
    private _entity: pc.Entity;

    private _pos: pc.Vec3 = new pc.Vec3();
    private _moveSpeed: number;
    private _rotSpeed: number;

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    constructor(root: pc.GraphNode) {
        if (!ParticlesEntity._materials) {
            // Vytvorit materialy
            this.createMaterials();

            // Buffer pro pozice
            ParticlesEntity._positionBuf = new Float32Array(3 * 4 * ParticlesEntity.MAX_PARTICLE_CNT);   // Kazda plane ma 4 vrcholy a jsou 3 pozice na vrchol

            for (let i = 0; i < ParticlesEntity.MAX_PARTICLE_CNT; i++) {
                let j = 3 * 4 * i;

                ParticlesEntity._positionBuf[j] = -0.5;
                ParticlesEntity._positionBuf[j + 1] = 0;
                ParticlesEntity._positionBuf[j + 2] = 0.5;

                ParticlesEntity._positionBuf[j + 3] = -0.5;
                ParticlesEntity._positionBuf[j + 4] = 0;
                ParticlesEntity._positionBuf[j + 5] = -0.5;

                ParticlesEntity._positionBuf[j + 6] = 0.5;
                ParticlesEntity._positionBuf[j + 7] = 0;
                ParticlesEntity._positionBuf[j + 8] = -0.5;

                ParticlesEntity._positionBuf[j + 9] = 0.5;
                ParticlesEntity._positionBuf[j + 10] = 0;
                ParticlesEntity._positionBuf[j + 11] = 0.5;
            }

            // Buffer pro indexy pozic
            ParticlesEntity._indexBuf = new Uint32Array(6 * ParticlesEntity.MAX_PARTICLE_CNT);  // Kazda plane ma 4 vrholy coz jsou 2 trojuhelniky, takze 6 indexu

            for (let i = 0; i < ParticlesEntity.MAX_PARTICLE_CNT; i++) {
                let vertId = i * 4;
                let j = i * 6;

                ParticlesEntity._indexBuf[j] = vertId;
                ParticlesEntity._indexBuf[j + 1] = vertId + 1;
                ParticlesEntity._indexBuf[j + 2] = vertId + 3;

                ParticlesEntity._indexBuf[j + 3] = vertId + 1;
                ParticlesEntity._indexBuf[j + 4] = vertId + 2;
                ParticlesEntity._indexBuf[j + 5] = vertId + 3;
            }

            // Buffer pro UVs
            ParticlesEntity._uvsBuf = new Float32Array(2 * 4 * ParticlesEntity.MAX_PARTICLE_CNT);    // Kazda plane ma 4 vrcholy a na 1 vrchol potrebuju 2 pozice

            for (let i = 0; i < ParticlesEntity.MAX_PARTICLE_CNT; i++) {
                let j = 2 * 4 * i;

                ParticlesEntity._uvsBuf[j] = 0;
                ParticlesEntity._uvsBuf[j + 1] = 0;

                ParticlesEntity._uvsBuf[j + 2] = 0;
                ParticlesEntity._uvsBuf[j + 3] = 1;

                ParticlesEntity._uvsBuf[j + 4] = 1;
                ParticlesEntity._uvsBuf[j + 5] = 1;

                ParticlesEntity._uvsBuf[j + 6] = 1;
                ParticlesEntity._uvsBuf[j + 7] = 0;
            }
        }

        // Vytvorit mesh
        this._mesh = pc.createMesh(Game.app.graphicsDevice,
            ParticlesEntity._positionBuf, {
            normals: new Float32Array(pc.calculateNormals(ParticlesEntity._positionBuf, ParticlesEntity._indexBuf)),
            uvs: ParticlesEntity._uvsBuf,
            indices: ParticlesEntity._indexBuf
        });

        // Vytvorit entitu
        let node = new pc.GraphNode();
        let meshInstance = new pc.MeshInstance(node, this._mesh, ParticlesEntity._materials[0]);
        let model = new pc.Model();

        model.graph = node;
        model.meshInstances.push(meshInstance);

        this._entity = new pc.Entity("tunnelParticles");
        this._entity.addComponent(pc.eCompomentType.model, {
            castShadows: false,
            castShadowsLightmap: false,
            receiveShadows: false
        });
        this._entity.model.model = model;
        this._entity.enabled = false;
        root.addChild(this._entity);
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    init(settings: ParticlesSettings, pos: pc.Vec3, offset: pc.Vec3): void {
        // Rychlost pohybu a rotace
        this._moveSpeed = rnd.randomIn(settings.moveMinSpeed, settings.moveMaxSpeed);
        this._rotSpeed = rnd.randomIn(settings.rotMinSpeed, settings.rotMaxSpeed);
        this._meshSizeZ = 0;

        // Pocet castic
        let parCnt = rnd.randomIntIn(settings.partMinCnt, settings.partMaxCnt);

        // Rozestup casti
        let parSpacing = (settings.partMaxAngle - settings.partMinAngle) / parCnt;
        let parSpacingVolition = (parSpacing * settings.partSpacingVolitionRatio) / 2;
        let parAngle = settings.partMinAngle;

        // Vytvorit jednotlive castice
        for (let parId = 0; parId < parCnt; parId++) {
            this.createParticle(parId, parAngle + rnd.randomIn(-parSpacingVolition, parSpacingVolition), settings);
            parAngle += parSpacing;
        }

        // Aktualizovat mesh
        this._mesh.setPositions(ParticlesEntity._positionBuf, 3, parCnt * 4);
        this._mesh.setIndices(ParticlesEntity._indexBuf, parCnt * 6);
        this._mesh.update();

        // Nastavit pozici a rotaci entity
        this._pos.copy(pos);
        this._pos.add(offset);
        this._entity.setPosition(this._pos);
        this._entity.setEulerAngles(0, 0, settings.rndRotation ? rnd.randomIn(0, 360) : 0);

        // Nastavit uniforms pro mesh
        let meshIns = this._entity.model.meshInstances[0];

        this._colorBuf[0] = settings.partColor.r;
        this._colorBuf[1] = settings.partColor.g;
        this._colorBuf[2] = settings.partColor.b;

        let posW = this._entity.getPosition();

        meshIns.setParameter("uColor", this._colorBuf);
        meshIns.setParameter("uMinAlphaZ", posW.z);
        meshIns.setParameter("uMaxAlphaZ", posW.z + 1);

        this._entity.enabled = true;
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    reset(): void {
        if (this._entity.enabled)
            this._entity.enabled = false;
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    update(delta: number, endZ: number): boolean {
        // Pohyb po ose Z
        if ((this._pos.z += this._moveSpeed * delta) - this._meshSizeZ >= endZ)
            return false;

        this._entity.setLocalPosition(this._pos);

        // Rotace
        if (this._rotSpeed != 0)
            this._entity.rotateLocal(0, 0, this._rotSpeed * delta);

        return true;
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    private createParticle(id: number, angle: number, settings: ParticlesSettings): void {
        angle *= pc.math.DEG_TO_RAD;

        let hLen = rnd.randomIn(settings.partMinLen, settings.partMaxLen) / 2
        let hW = settings.partWidth / 2;

        // Vypocitat pozici stredu plane
        let x = Math.cos(angle) * settings.radius;
        let y = Math.sin(angle) * settings.radius;
        let z = -rnd.randomIn(hLen, settings.entityMaxDepth - hLen);

        if (this._meshSizeZ < (z - hLen) * -1)
            this._meshSizeZ = (z - hLen) * -1;

        // Provest rotaci plane podel osy Z
        angle -= (Math.PI / 2); // Odecist 90 stupnu

        let offsetX = Math.cos(angle) * hW;
        let offsetY = Math.sin(angle) * hW;

        let positions = ParticlesEntity._positionBuf;
        let i = id * (3 * 4);  // Kazda plane ma 4 vrcholy a kazdy vrchol je x,y,z

        positions[i++] = x - offsetX;
        positions[i++] = y - offsetY;
        positions[i++] = z + hLen;

        positions[i++] = x - offsetX;
        positions[i++] = y - offsetY;
        positions[i++] = z - hLen;

        positions[i++] = x + offsetX;
        positions[i++] = y + offsetY;
        positions[i++] = z - hLen;

        positions[i++] = x + offsetX;
        positions[i++] = y + offsetY;
        positions[i] = z + hLen;
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    private createMaterials(): void {
        ParticlesEntity._materials = [];

        let gd = Game.app.graphicsDevice;
        let mat = new pc.Material();

        mat.blendType = pc.BLEND_NORMAL;
        mat.cull = pc.CULLFACE_NONE;
        mat.shader = new pc.Shader(gd, {
            attributes: {
                aPosition: pc.SEMANTIC_POSITION,
                aUv0: pc.SEMANTIC_TEXCOORD0
            },
            vshader: ParticlesEntity._MESH_V_SHADERS[0],
            fshader: "precision " + gd.precision + " float;\n" + ParticlesEntity._MESH_F_SHADERS[0]
        });
        mat.setParameter("uColor", [1, 1, 1]);
        mat.setParameter("uMinAlphaZ", 0);
        mat.setParameter("uMaxAlphaZ", 0);
        mat.update();

        ParticlesEntity._materials.push(mat);
    }
}